import React from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { Utensils, Users, TrendingUp, ArrowRight } from "lucide-react"
import api from "../services/api"
import { useDashboardFilters } from "../state/dashboardFilters"
import StatsOverview from "../components/StatsOverview"
import FoodSearchHealth from "../components/FoodSearchHealth"
import DatabaseStatus from "../components/DatabaseStatus"
import WorkoutChart from "../components/WorkoutChart"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Skeleton } from "../components/ui/skeleton"
import { Button } from "../components/ui/button"

// Food Logging Summary Card for Overview
function FoodLogSummary({ adminOnly }) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["foodLogStats", adminOnly],
    queryFn: () => api.getFoodLogStats(adminOnly),
    refetchInterval: 30000,
  })

  // Find top source
  const topSource = React.useMemo(() => {
    if (!stats?.countsBySource) return null
    const entries = Object.entries(stats.countsBySource)
    if (entries.length === 0) return null
    const [source, count] = entries.sort((a, b) => b[1] - a[1])[0]
    const sourceLabels = {
      photo_ai: "Photo AI",
      describe_ai: "Describe AI",
      database: "Database",
      barcode: "Barcode",
      favorite: "Favorites",
      custom_food: "Custom Food",
      custom_meal: "Custom Meal",
      quick_add: "Quick Add",
    }
    const percentage = stats.totalLogs > 0 ? ((count / stats.totalLogs) * 100).toFixed(0) : 0
    return { label: sourceLabels[source] || source, percentage }
  }, [stats])

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Utensils className="h-5 w-5" />
            Food Logging
          </CardTitle>
          <Link to="/nutrition">
            <Button variant="ghost" size="sm" className="gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Total Logs</p>
            {isLoading ? (
              <Skeleton className="h-7 w-16 mt-1" />
            ) : (
              <p className="text-xl font-bold tabular-nums">
                {stats?.totalLogs?.toLocaleString() || "0"}
              </p>
            )}
            {!isLoading && (
              <p className="text-xs text-muted-foreground">
                {stats?.logsToday || 0} today
              </p>
            )}
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Unique Users</p>
            {isLoading ? (
              <Skeleton className="h-7 w-16 mt-1" />
            ) : (
              <p className="text-xl font-bold tabular-nums">
                {stats?.uniqueUsers?.toLocaleString() || "0"}
              </p>
            )}
          </div>
          <div className="col-span-2">
            <p className="text-sm text-muted-foreground">Top Source</p>
            {isLoading ? (
              <Skeleton className="h-5 w-32 mt-1" />
            ) : topSource ? (
              <p className="text-sm font-medium">
                {topSource.label} ({topSource.percentage}%)
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">No data</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function OverviewPage() {
  const { adminOnly, autoRefresh } = useDashboardFilters()

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["stats", adminOnly],
    queryFn: () => api.getStats(adminOnly),
    refetchInterval: autoRefresh ? 30000 : false,
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Overview</h1>
        <p className="text-muted-foreground">
          Monitor your app's health and key metrics at a glance.
        </p>
      </div>

      {/* Health & Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <FoodSearchHealth />
          <DatabaseStatus />
          <FoodLogSummary adminOnly={adminOnly} />
        </div>
        <div>
          <StatsOverview stats={stats || {}} isLoading={statsLoading} />
        </div>
      </div>

      {/* Charts */}
      <div>
        <WorkoutChart data={stats?.dailyWorkouts} isLoading={statsLoading} />
      </div>
    </div>
  )
}
